import { useState } from "react";

import { Table, Card, Modal, Spin, Button, Typography, Tooltip, message, Progress } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";

import { useBreadcrumbs } from "context/useBreadcrumbs";
import api from "services/api.service";
import { RouteLinks } from "services/router.service";
import { useSharedStorageLinks } from "helpers/useSharedStorageLinks";
import { useSharedStorageMeta } from "helpers/useSharedStorageMeta";
import { GenerateLinkModal } from "components/common";
import { calculateExpiryDays, formatFileSize } from "helpers/calculateExpiryDays";
import copyBlueIcon from "data/icons/copy-blue.svg";
import { ISharedStorageLink } from "store/storage/storage.types";

import "./SharedDrive.scss";

const { Title, Text } = Typography;

const SharedDrivePage = () => {
  const [ isGenerateLinkOpen, setIsGenerateLinkOpen ] = useState(false);
  const { links, isLoading, error, refetch } = useSharedStorageLinks();
  const { meta, refetch: refetchMeta } = useSharedStorageMeta();
  const [ deletingSD, setDeletingSD ] = useState<any>(null);

  useBreadcrumbs([
    { url: RouteLinks.PROTECTED.EXTENSIONS.SHARED_DRIVE, label: "Extensions" },
    { label: "Shared Drive" },
  ]);

  const handleGenerateLinkSuccess = () => {
    refetch();
    refetchMeta();
  };

  const handleCopyLink = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      message.success("Link copied to clipboard!");
    } catch (err) {
      message.error("Failed to copy link");
    }
  };

  const onDeleteSD = (sd: ISharedStorageLink) => {
    setDeletingSD(sd);
  };

  const handleRevokeLink = async () => {
    try {
      await api.deleteSharedStorage(deletingSD.id);
      message.success("Link revoked successfully!");
      setDeletingSD(null);
      refetch();
    } catch (err) {
      message.error("Failed to revoke link");
    }
  };

  return (
    <div className="default-page-wrapper">
      <div className="shared-drive-content">
        <div className="shared-drive-header">
          <div className="header-content">
            { meta && (
              <div className="storage-info">
                <div className="storage-usage">
                  <Title level={ 1 }>Organization Quota</Title>
                  <Title level={ 3 }>{ formatFileSize(meta.used_drive_size) } of { formatFileSize(meta.max_drive_size) } used</Title>
                  <Progress
                    percent={ Math.round((meta.used_drive_size / meta.max_drive_size) * 100) }
                    size="small"
                    showInfo={ false }
                    strokeColor={ "#3E9CB0" }
                    trailColor={ "#f0f0f0" }
                  />
                </div>
              </div>
            ) }
          </div>
          <div className="header-buttons">
            <Button
              type="primary"
              icon={ <PlusOutlined/> }
              onClick={ () => setIsGenerateLinkOpen(true) }
            >
              Generate Shared Drive
            </Button>
          </div>
        </div>

        { isLoading ? (
          <div className="loading-container">
            <Spin size="large"/>
            <Text>Loading shared storage links...</Text>
          </div>
        ) : error ? (
          <div className="error-card">
            <span>Failed to load shared storage links. Please try again.</span>
            <Button type="primary" onClick={ refetch }>
              Retry
            </Button>
          </div>
        ) : links.length === 0 ? (
          <Card className="empty-card">
            <Text type="secondary">No shared storage links found.</Text>
          </Card>
        ) : (
          <Table
            dataSource={ links }
            columns={ [
              {
                title: "Link",
                key: "link",
                render: (_, record) => (
                  <div className="link-cell">
                    <div className="link-icon">
                      <Tooltip title="Click to copy URL">
                        <img
                          src={ copyBlueIcon }
                          alt="Copy link"
                          onClick={ () => handleCopyLink(record.link) }
                          style={ { cursor: "pointer", marginTop: 4 } }
                        />
                      </Tooltip>
                    </div>
                    <div className="link-info">
                      <div className="link-url">
                        <Tooltip title={ record.link }>
                          <a className="url-text" href={ record.link } target="_blank" rel="noreferrer">{ record.link }</a>
                        </Tooltip>
                      </div>
                    </div>
                  </div>
                ),
              },
              {
                title: "Info",
                key: "marker",
                width: 300,
                render: (value) => (
                  <span>{ value.marker }</span>
                ),
              },
              {
                title: "Expires",
                key: "expires",
                width: 120,
                render: (_, record) => (
                  <span>{ new Date(record.date_expire).toLocaleDateString() }</span>
                ),
              },
              {
                title: "Actions",
                key: "actions",
                width: 100,
                render: (_, record) => {
                  const expiryDays = calculateExpiryDays(record.date_expire);
                  const isExpired = expiryDays === 0;

                  return (
                    <div className="table-actions">
                      <Button
                        type="link"
                        icon={ <DeleteOutlined/> }
                        onClick={ () => onDeleteSD(record) }
                        className="revoke-button"
                        disabled={ isExpired }
                      >
                        Revoke
                      </Button>
                    </div>
                  );
                },
              },
            ] }
            pagination={ false }
            className="shared-links-table"
          />
        ) }
      </div>

      <GenerateLinkModal
        isGenerateLinkOpen={ isGenerateLinkOpen }
        setIsGenerateLinkOpen={ setIsGenerateLinkOpen }
        onSuccess={ handleGenerateLinkSuccess }
      />

      <Modal
        className="user-accept-modal-wrapper"
        open={ !!deletingSD }
        onCancel={ () => setDeletingSD(null) }
        footer={ null }
        width={ 500 }
      >
        <>
          <h4 className="modal-title">
            Are you sure you want to revoke link
            {" "}
            <span>{ deletingSD?.link } </span>?
          </h4>

          <div className="action-wrapper">
            <Button
              type="primary"
              onClick={ handleRevokeLink }
            >
              Yes
            </Button>

            <Button onClick={ () => setDeletingSD(null) }>
              No
            </Button>
          </div>
        </>
      </Modal>
    </div>
  );
};

export default SharedDrivePage;
